import type { ErrorInfo, ReactNode } from 'react';
import React from 'react';
import Logger from '../../utils/Logger';

// https://github.com/facebook/react/issues/11538
const isCausedByTranslation = (error: Error) => {
  const stack = error.stack || '';
  const isReactDOMMutationError =
    stack.includes('removeChild') || stack.includes('insertBefore');
  if (!isReactDOMMutationError) return false;

  const isPageTranslated = !!document.querySelector(
    [
      // Google Translate
      '.translated-ltr',
      '.translated-rtl',
      // Edge built-in translation
      '[_msttexthash]',
    ].join(),
  );
  return isPageTranslated;
};

interface FatalErrorBoundaryProps {
  children: ReactNode;
}

interface FatalErrorBoundaryState {
  hasError: boolean;
  isCausedByTranslation: boolean;
}

class FatalErrorBoundary extends React.Component<
  FatalErrorBoundaryProps,
  FatalErrorBoundaryState
> {
  static getDerivedStateFromError(error: Error): FatalErrorBoundaryState {
    return {
      hasError: true,
      isCausedByTranslation: isCausedByTranslation(error),
    };
  }

  constructor(props: FatalErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isCausedByTranslation: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (!isCausedByTranslation(error)) {
      Logger.fatal(error, errorInfo, { tags: { errorBoundary: 'fatal' } });
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const tryAgainLink = (
      <a // eslint-disable-line jsx-a11y/anchor-is-valid
        href="#"
        onClick={(e) => {
          e.preventDefault();
          window.location.reload();
        }}
      >
        try again
      </a>
    );

    const contactUsLink = <a href="mailto:support@slab.com">contact us</a>;

    return (
      <div style={{ margin: 12 }}>
        <h2>Oops! Something went wrong.</h2>
        {this.state.isCausedByTranslation ? (
          <>
            <p>
              The error is most likely caused by a translation tool. Please
              disable it and {tryAgainLink}.
            </p>
            <p>If the problem persists, feel free to {contactUsLink}.</p>
          </>
        ) : (
          <>
            <p>The error has been logged and we’ll be on it!</p>
            <p>
              Please {tryAgainLink} later or {contactUsLink} if the problem
              persists.
            </p>
          </>
        )}
      </div>
    );
  }
}

export default FatalErrorBoundary;
